import { useState, useRef, useCallback } from "react";
import {
  HubConnectionBuilder,
  LogLevel,
  HubConnection,
  HubConnectionState,
} from "@microsoft/signalr";
import {
  ITransactionUploadProgress,
  ITransactionUploadSummary,
} from "../types/models";
import { uploadExcelFile } from "../store/modules/transactions/transactionActions";
import { DuplicateActionEnumModel } from "../types/types";

interface UseTransactionUploadProps {
  accessToken: string | undefined | null;
  apiUrl: string;
}

interface UseTransactionUploadResult {
  isUploading: boolean;
  uploadProgress: ITransactionUploadProgress | null;
  uploadSummary: ITransactionUploadSummary | null;
  connectionError: string | null;
  handleUpload: (
    file: File,
    ignoreDuplicates: DuplicateActionEnumModel,
    enablePropMixLookup?: boolean
  ) => Promise<void>;
  resetStates: () => void;
}

export const useTransactionUpload = ({
  accessToken,
  apiUrl,
}: UseTransactionUploadProps): UseTransactionUploadResult => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] =
    useState<ITransactionUploadProgress | null>(null);
  const [uploadSummary, setUploadSummary] =
    useState<ITransactionUploadSummary | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const connectionRef = useRef<HubConnection | null>(null);

  const cleanupConnection = useCallback(async () => {
    if (
      connectionRef.current &&
      connectionRef.current.state === HubConnectionState.Connected
    ) {
      try {
        await connectionRef.current.stop();
      } catch (error) {
        console.error("Error stopping connection:", error);
      }
      connectionRef.current = null;
    }
  }, []);

  const handleUpload = async (
    file: File,
    ignoreDuplicates: DuplicateActionEnumModel,
    enablePropMixLookup: boolean = false
  ) => {
    if (!file) {
      setConnectionError(
        "No file selected. Please select an Excel file to upload."
      );
      return;
    }
    if (!accessToken) {
      setConnectionError("Access token is missing. Please log in again.");
      return;
    }

    setIsUploading(true);
    setUploadProgress(null);
    setUploadSummary(null);
    setConnectionError(null);

    // Clean up any existing connection
    await cleanupConnection();

    // Create SignalR connection
    // const connection = new HubConnectionBuilder()
    //   .withUrl(`${apiUrl}/hubs/transactionUpload`, {
    //     headers: {
    //       "Content-Type": "application/json",
    //       "Access-Control-Allow-Origin": "*",
    //       Authorization: `Bearer ${accessToken}`,
    //     },
    //   })
    //   .configureLogging(LogLevel.Debug)
    //   .withAutomaticReconnect()
    //   .build();
    const connection = new HubConnectionBuilder()
      .withUrl(`${apiUrl}/hubs/transactionUpload`, {
        accessTokenFactory: () => accessToken || "",
      })
      .configureLogging(LogLevel.Debug)
      .withAutomaticReconnect()
      .build();

    connectionRef.current = connection;

    try {
      // Register error handlers before starting connection
      connection.onclose((error) => {
        console.log("Connection closed:", error);
        if (error && isUploading) {
          setConnectionError("Connection lost. Please try again.");
          setIsUploading(false);
        }
      });

      connection.onreconnecting((error) => {
        console.warn("Connection reconnecting:", error);
        setConnectionError(
          "Connection interrupted. Attempting to reconnect..."
        );
      });

      connection.onreconnected((connectionId) => {
        console.log("Connection reestablished:", connectionId);
        setConnectionError(null);
      });

      // Start connection
      await connection.start();

      // Register event handlers
      connection.on(
        "ReceiveProgressUpdate",
        (progress: ITransactionUploadProgress) => {
          console.log("Progress update:", progress);
          setUploadProgress(progress);
        }
      );

      connection.on(
        "ReceiveUploadSummary",
        (summary: ITransactionUploadSummary) => {
          console.log("Upload summary:", summary);
          setUploadSummary(summary);

          if (summary.isComplete) {
            setIsUploading(false);
            cleanupConnection();
          }
        }
      );

      // Upload the file with duplicate handling option and optional PropMix lookup
      await uploadExcelFile(file, ignoreDuplicates, enablePropMixLookup);
    } catch (error) {
      console.error("SignalR Connection Error:", error);
      setConnectionError(
        "Failed to connect to the server. Please check your network connection and try again."
      );
      setIsUploading(false);
      await cleanupConnection();
    }
  };

  // Function to reset all states
  const resetStates = useCallback(() => {
    setIsUploading(false);
    setUploadProgress(null);
    setUploadSummary(null);
    setConnectionError(null);
    cleanupConnection();
  }, [cleanupConnection]);

  return {
    isUploading,
    uploadProgress,
    uploadSummary,
    connectionError,
    handleUpload,
    resetStates,
  };
};
